import type { CSSProperties } from "react";
import { color, radius } from "@/lib/design-tokens";
import { googleReviewProof } from "@/lib/copy/reviews";

interface StarRatingProps {
  value: number;
  size?: number | string;
  decorative?: boolean;
}

/** A fractional five-star rating with one accessible label, not five glyphs. */
export function StarRating({ value, size = 16, decorative = false }: StarRatingProps) {
  const clamped = Math.max(0, Math.min(5, value));
  const label = `${clamped} out of 5 stars`;

  return (
    <span
      {...(decorative ? { "aria-hidden": true } : { role: "img", "aria-label": label })}
      style={{
        position: "relative",
        display: "inline-block",
        flex: "none",
        fontSize: size,
        lineHeight: 1,
        letterSpacing: ".08em",
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true" style={{ color: color.borderCardHover }}>
        ★★★★★
      </span>
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: "0 auto 0 0",
          width: `${(clamped / 5) * 100}%`,
          overflow: "hidden",
          color: color.gold,
          whiteSpace: "nowrap",
        }}
      >
        ★★★★★
      </span>
    </span>
  );
}

type RatingBadgeVariant = "hero" | "heroPlain" | "light" | "footer" | "section";

const BADGE_STYLE: Record<RatingBadgeVariant, CSSProperties> = {
  hero: {
    color: color.white,
    background: "rgba(0,40,104,.72)",
    border: "1px solid rgba(255,255,255,.35)",
  },
  /**
   * The hero's proof line, unboxed.
   *
   * `hero` draws a filled, bordered pill. Stacked under the two hero buttons
   * that is a third outlined rectangle in a column of outlined rectangles, and
   * the reading order stops being headline → sentence → action and becomes a
   * list of four widgets. The rating is evidence for the button above it, not a
   * fourth control, so it drops its box and sits as a line of type. The scrim
   * under it resolves to rgba(0,40,104,.94), which is where the contrast comes
   * from now that the badge no longer carries its own ground.
   *
   * Padding is zeroed so the stars align to the same left edge as the button
   * row rather than sitting 12px inside it.
   */
  heroPlain: {
    color: color.white,
    background: "transparent",
    border: "none",
    padding: 0,
  },
  light: {
    color: color.blue,
    background: color.white,
    border: `1px solid ${color.borderCard}`,
  },
  footer: {
    color: color.white,
    background: "rgba(255,255,255,.08)",
    border: "1px solid rgba(255,255,255,.22)",
  },
  section: {
    color: color.blue,
    background: color.white,
    border: `1px solid ${color.borderCard}`,
  },
};

/** Compact, reusable proof point that always links to the public source. */
export function GoogleRatingBadge({ variant }: { variant: RatingBadgeVariant }) {
  const { rating, reviewCount, listingHref } = googleReviewProof;

  return (
    <a
      href={listingHref}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Rated ${rating} out of 5 from ${reviewCount} Google reviews. Open Google reviews in a new tab.`}
      data-google-rating={variant}
      className="bb-google-rating"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "9px",
        width: "fit-content",
        maxWidth: "100%",
        padding: "8px 12px",
        borderRadius: radius.button,
        fontSize: "14px",
        fontWeight: 700,
        lineHeight: 1.25,
        // Spread last so a variant can override geometry, not just ink. Only
        // `heroPlain` does (padding: 0); every other variant sets colour alone,
        // so their rendered box is byte-identical to before this moved.
        ...BADGE_STYLE[variant],
      }}
    >
      <StarRating value={rating} size={14} decorative />
      <span>
        {rating} on Google · {reviewCount} reviews
      </span>
    </a>
  );
}
